import { useState, useEffect, useCallback, useRef } from "react";
import { Star, Facebook, Instagram, ExternalLink, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const TRUSTPILOT_URL = "https://dk.trustpilot.com/review/eservice-jylland.dk";
const TRUSTPILOT_COUNT = 5;

// Real Trustpilot anmeldelser — opdater manuelt når nye anmeldelser kommer ind.
const REVIEWS = [
  {
    name: "Asbjørn Lind Krogh",
    initials: "AL",
    meta: "DK · 1 anmeldelse",
    date: "20. august 2026",
    title: "Professionelle og meget mere",
    text: "Jeg havde et rigtig godt indtryk med Service Pro. Super professionelle og meget flinke folk som gjorde det til en meget tryg og helstøbt oplevelse at få lavet bilen. Det var meget som et normalt værksted, og så alligevel ikke; mega smart at mekanikeren kan komme til kunden og lave bilen derhjemme, på arbejdet eller hvor det skal være. 5/5 herfra.",
  },
  {
    name: "Daniel Skov",
    initials: "DS",
    meta: "DK · 3 anmeldelser",
    date: "30. juli 2026",
    title: "Fantastisk service",
    text: "Fantastisk service, godt udført arbejde, fantastisk kommunikation, og så er det jo genialt at Kasper kommer ud på ens egen adresse. Vil jeg helt sikkert benytte igen næste gang!!",
  },
  {
    name: "Britt Larsen",
    initials: "BL",
    meta: "DK · 2 anmeldelser",
    date: "6. juli 2026",
    title: "Perfekt og virkelig smart",
    text: "Det her er mega smart, havde en super oplevelse og vil helt sikkert bruge igen. Min bil begyndte at blinke i mange advarselslamper, det var lidt sent og hvad gør man så. Jeg fik anbefalet Service Pro v/Kasper som er kørende mekaniker — han kom hjem til mig, fandt fejlene, gav mig et tilbud og kom og lavede bilen hjemme. Virkelig dygtig mekaniker, kom til tiden og vups var jeg kørende igen.",
  },
  {
    name: "Thea Nedergaard Mortensen",
    initials: "TN",
    meta: "DK · 3 anmeldelser",
    date: "13. juli 2026",
    title: "10/10 oplevelse",
    text: "Jeg fik lavet service på min arbejdsplads! Jeg har i flere omgange fået fikset min bil hos dem, og jeg går derfra med en rigtig god oplevelse hver gang. Denne gang kunne Kasper tilbyde at komme ud på min arbejdsplads og lave service på bilen, mens jeg var på arbejde. Det gjorde det helt klart meget nemmere for mig. Tusind tak for den altid gode service!",
  },
];

const AVATAR_COLORS = ["#00F0FF", "#FF3D71", "#00B67A", "#FFC542"];

export const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
  </svg>
);

export const TrustpilotStar = () => (
  <div className="w-6 h-6 bg-[#00B67A] flex items-center justify-center rounded-[2px]">
    <Star className="w-4 h-4 fill-white text-white" strokeWidth={0} />
  </div>
);

export const FacebookIcon = () => <Facebook className="w-6 h-6 text-[#1877F2]" fill="#1877F2" strokeWidth={0} />;
export const InstagramIcon = () => <Instagram className="w-6 h-6 text-[#E4405F]" strokeWidth={2} />;

// Shared social/review destinations — reused by the header dropdown social bar.
// The user will plug in their real account URLs later.
export const SOCIAL = {
  trustpilot: { Icon: TrustpilotStar, label: "Trustpilot", href: TRUSTPILOT_URL },
  google: { Icon: GoogleG, label: "Google", href: "https://g.page/r/CSLba1BcjtJ7EBM/review" },
  facebook: { Icon: FacebookIcon, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61594008368002" },
  instagram: { Icon: InstagramIcon, label: "Instagram", href: "https://www.instagram.com/eservicejylland/" },
};

const SLIDE_DURATION = 6000;
const COMPACT_SLIDE_DURATION = 8000;

const ReviewRow = ({ compact = false }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((next) => {
    setDirection(next > index || (next === 0 && index === REVIEWS.length - 1) ? 1 : -1);
    setIndex(next);
  }, [index]);

  const next = useCallback(() => {
    setDirection(1);
    setIndex((i) => (i + 1) % REVIEWS.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setIndex((i) => (i - 1 + REVIEWS.length) % REVIEWS.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(next, compact ? COMPACT_SLIDE_DURATION : SLIDE_DURATION);
    return () => clearInterval(timerRef.current);
  }, [isPaused, next, compact]);

  const review = REVIEWS[index];
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  if (compact) {
    return (
      <div
        className="max-w-2xl mx-auto"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid="review-row-section-compact"
      >
        {/* Tiny social strip — same destinations as the header menu, minimal footprint */}
        <div className="flex items-center justify-center gap-4 mb-3" data-testid="review-row-social-strip">
          {Object.entries(SOCIAL).map(([key, s]) => (
            <a
              key={key}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`review-row-social-${key}`}
              className="opacity-50 hover:opacity-100 transition-opacity duration-300 scale-75 inline-block"
            >
              <s.Icon />
            </a>
          ))}
        </div>

        <a
          href={TRUSTPILOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="review-slideshow-compact"
          className="group relative flex flex-col gap-2 backdrop-blur-md bg-black/70 border border-white/10 rounded-2xl px-5 py-3.5 sm:px-6 sm:py-4 hover:border-[#00F0FF]/40 hover:bg-black/80 transition-colors duration-300"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <TrustpilotStar />
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3 h-3 fill-[#00B67A] text-[#00B67A]" strokeWidth={0} />
                ))}
              </div>
              <span className="text-white text-xs sm:text-sm font-bold" style={{ fontFamily: "Saira" }}>Trustpilot</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {REVIEWS.map((r, i) => (
                <span
                  key={r.name}
                  data-testid={`review-dot-compact-${i}`}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === index ? "w-4 bg-[#00F0FF]" : "w-1 bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="relative min-h-[4.2rem] sm:min-h-[3.9rem] overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={index}
                custom={direction}
                initial={{ opacity: 0, y: direction > 0 ? 10 : -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: direction > 0 ? -10 : 10 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
                data-testid={`review-slide-compact-${index}`}
              >
                <p className="text-white text-xs sm:text-sm leading-relaxed line-clamp-3">"{review.text}"</p>
                <p className="text-[#A0AEC0] text-[11px] sm:text-xs mt-1 font-medium">— {review.name}, {review.date}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </a>
      </div>
    );
  }

  return (
    <section className="py-14 md:py-20 bg-[#0A0A0A] reveal-on-scroll relative overflow-hidden" data-testid="review-row-section">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00F0FF]/[0.04] rounded-full blur-3xl pointer-events-none" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-10">
          <p className="text-[#00F0FF] text-xs font-bold tracking-[0.3em] uppercase mb-4">Tilfredse kunder</p>
          <h2 className="text-2xl sm:text-3xl font-light text-white tracking-tight mb-4">Det siger vores kunder</h2>
          <a
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="review-platform-trustpilot"
            className="group inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 hover:border-[#00B67A]/50 hover:bg-white/[0.06] transition-colors duration-300"
          >
            <TrustpilotStar />
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#00B67A] text-[#00B67A]" strokeWidth={0} />
              ))}
            </div>
            <span className="text-white font-bold text-sm" style={{ fontFamily: "Saira" }}>5,0 / 5</span>
            <span className="text-[#A0AEC0] text-xs">· {TRUSTPILOT_COUNT} anmeldelser</span>
            <ExternalLink className="w-3 h-3 text-[#00F0FF] opacity-0 group-hover:opacity-100 -ml-1 transition-opacity" />
          </a>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          data-testid="review-slideshow"
        >
          <div className="relative min-h-[280px] sm:min-h-[240px] overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={index}
                custom={direction}
                initial={{ opacity: 0, x: direction > 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -60 : 60 }}
                transition={{ duration: 0.45, ease: "easeInOut" }}
                className="bg-white/[0.03] border border-white/10 rounded-xl p-6 sm:p-8"
                data-testid={`review-slide-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center font-black text-sm shrink-0"
                      style={{ backgroundColor: `${avatarColor}22`, color: avatarColor, fontFamily: "Saira" }}
                    >
                      {review.initials}
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm" style={{ fontFamily: "Saira" }}>{review.name}</p>
                      <p className="text-[#A0AEC0] text-xs">{review.meta} · {review.date}</p>
                    </div>
                  </div>
                  <Quote className="w-7 h-7 text-white/10 shrink-0 hidden sm:block" strokeWidth={1.5} />
                </div>
                <div className="flex gap-1 mb-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#00B67A] text-[#00B67A]" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-white font-bold text-base mb-2" style={{ fontFamily: "Saira" }}>{review.title}</p>
                <p className="text-[#A0AEC0] text-sm leading-relaxed">{review.text}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prev}
              aria-label="Forrige anmeldelse"
              data-testid="review-nav-prev"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              {REVIEWS.map((r, i) => (
                <button
                  key={r.name}
                  onClick={() => goTo(i)}
                  aria-label={`Gå til anmeldelse ${i + 1}`}
                  data-testid={`review-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-6 bg-[#00F0FF]" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              aria-label="Næste anmeldelse"
              data-testid="review-nav-next"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Tiny social strip — same destinations as the header menu, minimal footprint */}
          <div className="flex items-center justify-center gap-4 mt-5" data-testid="review-row-social-strip">
            {Object.entries(SOCIAL).map(([key, s]) => (
              <a
                key={key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={`review-row-social-${key}`}
                className="opacity-50 hover:opacity-100 transition-opacity duration-300 scale-75 inline-block"
              >
                <s.Icon />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewRow;
